import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuditDashboardReceiptsByDate } from "../../../hooks";
import {
  buildAuditDashboardReceiptReviewUrl,
  formatAuditDashboardPosReceiptSummary,
  getAuditDashboardReceiptGroups,
} from "../../../utils";
import AuditReceiptImageCard from "./AuditReceiptImageCard";

interface ShiftReceiptSectionProps {
  storeId?: string;
  auditId?: string | null;
  reportDate?: string | null;
  enabled: boolean;
}

const receiptCardSx = {
  flex: "1 1 calc(50% - 12px)",
  maxWidth: 450,
  minWidth: 320,
} as const;

export default function ShiftReceiptSection({ storeId, auditId, reportDate, enabled }: ShiftReceiptSectionProps) {
  const [hiddenReceiptKeys, setHiddenReceiptKeys] = useState<string[]>([]);
  const receiptsQuery = useAuditDashboardReceiptsByDate(storeId, reportDate, enabled && Boolean(auditId));
  const receiptGroups = useMemo(
    () => getAuditDashboardReceiptGroups(receiptsQuery.data, auditId),
    [auditId, receiptsQuery.data]
  );
  const receiptItems = useMemo(
    () => [
      ...receiptGroups.inventoryReceipts.map((receipt) => ({
        key: receipt.key,
        queueId: receipt.queueId,
        title: receipt.title,
        caption: receipt.caption,
        reviewUrl: buildAuditDashboardReceiptReviewUrl(storeId, receipt.queueId),
        loadingText: "Loading inventory receipt...",
      })),
      ...receiptGroups.salesReceiptsInPeriod.map((receipt) => ({
        key: `sales-in-period-${receipt.receiptAuditQueueId}`,
        queueId: receipt.receiptAuditQueueId,
        title: "Sales in Period",
        caption: formatAuditDashboardPosReceiptSummary(receipt),
        reviewUrl: buildAuditDashboardReceiptReviewUrl(storeId, receipt.receiptAuditQueueId),
        loadingText: "Loading POS receipt...",
      })),
      ...receiptGroups.salesReceiptsAroundPeriod.map((receipt) => ({
        key: `sales-around-period-${receipt.receiptAuditQueueId}`,
        queueId: receipt.receiptAuditQueueId,
        title: "Sales Before / After Period",
        caption: formatAuditDashboardPosReceiptSummary(receipt),
        reviewUrl: buildAuditDashboardReceiptReviewUrl(storeId, receipt.receiptAuditQueueId),
        loadingText: "Loading POS receipt...",
      })),
    ],
    [receiptGroups.inventoryReceipts, receiptGroups.salesReceiptsAroundPeriod, receiptGroups.salesReceiptsInPeriod, storeId]
  );
  const receiptItemKeySignature = receiptItems.map((item) => item.key).join("|");

  const hasReceipts = receiptItems.length > 0;
  const allReceiptCardsHidden =
    hasReceipts && receiptItems.every((item) => hiddenReceiptKeys.includes(item.key));

  useEffect(() => {
    setHiddenReceiptKeys([]);
  }, [auditId, receiptItemKeySignature, reportDate]);

  const handleReceiptVisibilityChange = useCallback((key: string, isVisible: boolean) => {
    setHiddenReceiptKeys((currentKeys) => {
      const isHidden = currentKeys.includes(key);

      if (isVisible) {
        return isHidden ? currentKeys.filter((currentKey) => currentKey !== key) : currentKeys;
      }

      return isHidden ? currentKeys : [...currentKeys, key];
    });
  }, []);

  if (!enabled || !auditId) {
    return null;
  }

  if (receiptsQuery.isLoading || (receiptsQuery.isFetching && !receiptsQuery.data)) {
    return (
      <Box sx={{ minHeight: 96, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (receiptsQuery.isError) {
    return (
      <Box sx={{ minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography component="div" sx={{ fontSize: 12, color: "text.secondary" }}>
          Unable to load receipts.
        </Typography>
      </Box>
    );
  }

  if (!hasReceipts || allReceiptCardsHidden) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "start",
        justifyContent: "start",
        columnGap: 3,
        rowGap: 2.5,
        pt: 2,
        px: 1.5,
        pb: 1.5,
        bgcolor: "background.paper",
        backgroundImage: "none",
      }}
    >
      {receiptItems.map((receipt) => (
        <AuditReceiptImageCard
          key={receipt.key}
          queueId={receipt.queueId}
          title={receipt.title}
          caption={receipt.caption}
          reviewUrl={receipt.reviewUrl}
          loadingText={receipt.loadingText}
          containerSx={receiptCardSx}
          visibilityKey={receipt.key}
          onVisibilityChange={handleReceiptVisibilityChange}
        />
      ))}
    </Box>
  );
}
